import { CloseCircleOutlined, CloseOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import * as React from "react";
import { KeyedImgFrame } from "src/business/attribute/attrEdits/AttrExternalImages/KeyedImgFrame";
import { classNames } from "src/common/classNames";
import { onError } from "src/common/onError";

import * as styles from "./ImageDetails.module.less";

type PropsImageDetails = {
  frames: KeyedImgFrame[];
  close: () => void;
  startIndex?: number;
};

type ImgStatus = "error" | "loading" | "ok";

export const ImageDetails: React.FC<PropsImageDetails> = (props) => {
  const { frames, close, startIndex = 0 } = props;
  const { length } = frames;
  const [index, setIndex] = React.useState(startIndex);
  const currentFrame = frames[index];
  const currentUrl = currentFrame?.url;

  const [statuses, setStatuses] = React.useState<Record<string, ImgStatus>>({});
  const setStatus = (url: string | undefined, status: ImgStatus) => {
    if (url) setStatuses((prev) => ({ ...prev, [url]: status }));
  };
  const currentStatus = currentUrl ? statuses[currentUrl] : undefined;

  React.useEffect(() => {
    setStatus(currentUrl, "loading");
  }, [currentUrl]);

  const onLeft = () =>
    setIndex((prevIndex) => (prevIndex === 0 ? length - 1 : prevIndex - 1));
  const onRight = () =>
    setIndex((prevIndex) => (prevIndex === length - 1 ? 0 : prevIndex + 1));

  const onKey = (e: KeyboardEvent) => {
    if (e.code === "Escape") {
      e.stopPropagation();
      e.preventDefault();
      close();
    } else if (e.code === "ArrowLeft" && length > 1) onLeft();
    else if (e.code === "ArrowRight" && length > 1) onRight();
  };
  React.useEffect(() => {
    // Важно перехватывать событие до того как на него отреагируют открытые модалки.
    // Иначе нажатие Escape закрывает не только просмотр картинок, но и модалку, что может привести к потере вводимых данных.
    const useCapture = true;
    document.addEventListener("keydown", onKey, useCapture);
    return () => {
      document.removeEventListener("keydown", onKey, useCapture);
      close();
    };
  }, []);
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={styles.overlay}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <div className={styles.sliderTop}>
        <div className={styles.sliderBorder}>
          {currentFrame?.label && (
            <div className={styles.nameBox}>
              <div className={styles.name}>{currentFrame.label}</div>
            </div>
          )}
          <button
            type="button"
            className={classNames([styles.closeButton, styles.obutton])}
            onClick={close}
          >
            <CloseOutlined />
          </button>
          <button
            type="button"
            className={classNames([styles.leftButton, styles.obutton])}
            onClick={onLeft}
          >
            &lt;
          </button>
          <button
            type="button"
            className={classNames([styles.rightButton, styles.obutton])}
            onClick={onRight}
          >
            &gt;
          </button>
          <div className={styles.info}>
            Изображение {index + 1} из {length}
          </div>
          {currentStatus === "error" ? (
            <CloseCircleOutlined style={{ fontSize: "32px", color: "red" }} />
          ) : (
            <>
              <img
                className={styles.frame}
                src={currentUrl}
                alt=""
                onLoad={() => setStatus(currentUrl, "ok")}
                onError={() => {
                  onError(new Error("Ошибка при загрузке изображения"));
                  setStatus(currentUrl, "error");
                }}
              />
              {currentStatus === "loading" && (
                <Spin size="large" className={styles.spin} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
